import React from 'react';
import { LaunchMonitorReviewData } from '../services/postFittingReviewService';

interface PostFittingLaunchMonitorProps {
  equipmentType: 'driver' | 'iron' | 'wedge';
  clubLabel?: string;
  data: LaunchMonitorReviewData;
  onChange: (data: LaunchMonitorReviewData) => void;
}

// Only the 6 fields used by the scoring model
const RANGES: Record<string, Record<string, { min: number; max: number; step: number; label: string; hint: string }>> = {
  driver: {
    launch_angle:        { min: 8,    max: 18,   step: 0.1, label: 'Launch Angle (deg)',        hint: 'Feeds loft score (15%). Optimal: 10-14°' },
    spin_rate:           { min: 1500, max: 4000, step: 1,   label: 'Spin Rate (rpm)',            hint: 'Feeds shaft score via tip stiffness (30%). Optimal: 2000-2800' },
    ball_speed:          { min: 120,  max: 185,  step: 0.1, label: 'Ball Speed (mph)',           hint: 'Energy transfer efficiency. Every 1 mph ~ 2 yards' },
    carry_distance:      { min: 180,  max: 320,  step: 1,   label: 'Carry Distance (yds)',       hint: 'Feeds flex score (20%). Air distance only' },
    smash_factor:        { min: 1.40, max: 1.52, step: 0.01,label: 'Smash Factor',               hint: '1.48-1.50 = excellent energy transfer' },
    carry_std_dev: { min: 0, max: 40,  step: 0.1, label: 'Carry Consistency (std dev, yds)', hint: 'Lower = tighter dispersion. <10 yds = excellent' },
  },
  iron: {
    launch_angle:        { min: 12,   max: 22,   step: 0.1, label: 'Launch Angle (deg)',        hint: 'Feeds loft score (15%). Optimal: 15-18°' },
    spin_rate:           { min: 4000, max: 8500, step: 1,   label: 'Spin Rate (rpm)',            hint: 'Feeds shaft score via tip stiffness (30%). Optimal: 6000-7000' },
    ball_speed:          { min: 85,   max: 135,  step: 0.1, label: 'Ball Speed (mph)',           hint: 'Tour avg: ~110 mph' },
    carry_distance:      { min: 100,  max: 200,  step: 1,   label: 'Carry Distance (yds)',       hint: 'Feeds flex score (20%). Air distance only' },
    smash_factor:        { min: 1.20, max: 1.45, step: 0.01,label: 'Smash Factor',               hint: '1.38+ = excellent energy transfer' },
    carry_std_dev: { min: 0, max: 30,  step: 0.1, label: 'Carry Consistency (std dev, yds)', hint: 'Lower = tighter. <8 yds = excellent' },
  },
  wedge: {
    launch_angle:        { min: 20,   max: 35,   step: 0.1, label: 'Launch Angle (deg)',        hint: 'Feeds loft score (15%). Optimal: 24-28°' },
    spin_rate:           { min: 6000, max: 12000,step: 1,   label: 'Spin Rate (rpm)',            hint: 'Feeds shaft score via tip stiffness (30%). Optimal: 8000-10000' },
    ball_speed:          { min: 60,   max: 110,  step: 0.1, label: 'Ball Speed (mph)',           hint: 'Full swing: 70-95 mph' },
    carry_distance:      { min: 50,   max: 140,  step: 1,   label: 'Carry Distance (yds)',       hint: 'Feeds flex score (20%). Air distance only' },
    smash_factor:        { min: 1.00, max: 1.30, step: 0.01,label: 'Smash Factor',               hint: '1.20+ = excellent energy transfer' },
    carry_std_dev: { min: 0, max: 25,  step: 0.1, label: 'Carry Consistency (std dev, yds)', hint: 'Lower = tighter. <6 yds = excellent' },
  },
};

const FIELDS = ['launch_angle', 'spin_rate', 'ball_speed', 'carry_distance', 'smash_factor', 'carry_std_dev'];

const PostFittingLaunchMonitor: React.FC<PostFittingLaunchMonitorProps> = ({
  equipmentType,
  clubLabel,
  data,
  onChange,
}) => {
  const ranges = RANGES[equipmentType];
  const inputClass = "mt-1 block w-full text-base px-4 py-2 rounded-md shadow-sm border border-gray-300 focus:border-green-ink focus:ring-green-ink";

  const handleChange = (field: string, value: any) => {
    onChange({ ...data, [field]: value });
  };

  const title = clubLabel || (equipmentType === 'driver' ? 'Driver' : equipmentType === 'iron' ? '7-Iron' : 'Wedge');

  return (
    <div className="space-y-4">
      <h4 className="font-medium text-gray-900 text-sm">
        {title} — Launch Monitor Data
        <span className="ml-2 text-xs text-gray-400 font-normal">(6 key fields used by scoring model)</span>
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {FIELDS.map(field => {
          const config = ranges[field];
          if (!config) return null;
          const val = (data as any)[field];
          return (
            <div key={field}>
              <label className="block text-sm font-medium text-gray-700 mb-1">{config.label}</label>
              <input
                type="number"
                min={config.min}
                max={config.max}
                step={config.step}
                value={val ?? ''}
                onChange={(e) => handleChange(field, e.target.value ? parseFloat(e.target.value) : undefined)}
                className={inputClass}
              />
              <p className="text-xs text-gray-500 mt-1">{config.hint}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PostFittingLaunchMonitor;
